<template>
  <div class="document-viewer">
    <div v-if="loading" class="loading-container">
      <a-spin size="large">
        <template #indicator>
          <LoadingOutlined style="font-size: 24px" spin />
        </template>
      </a-spin>
      <p>正在加载文档...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <a-result
        status="error"
        title="文档加载失败"
        :sub-title="error"
      >
        <template #extra>
          <a-button type="primary" @click="loadDocument">
            重新加载
          </a-button>
        </template>
      </a-result>
    </div>

    <div
      v-show="!error"
      class="document-content"
      ref="contentContainer"
      @scroll="handleScroll"
    >
      <!-- PDF 预览（后端 LibreOffice 转 PDF）；加载时也渲染容器，否则 loadDocument 里 pdfPagesContainer 为 null 无法发起请求 -->
      <div
        v-if="fileType === 'pdf' && documentUrl && isMounted"
        class="pdf-pages"
        ref="pdfPagesContainer"
      />
      <div v-else-if="documentUrl && fileType !== 'pdf'" class="unsupported-type">
        <a-result
          status="warning"
          title="不支持的文件格式"
          :sub-title="`当前仅支持 PDF 预览，格式 .${fileType} 请下载后查看`"
        >
          <template #extra>
            <a-button type="primary" @click="handleDownload">
              下载文档
            </a-button>
          </template>
        </a-result>
      </div>
      <div v-else-if="!documentUrl" class="unsupported-type">
        <a-empty description="文档未加载" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import { LoadingOutlined } from '@ant-design/icons-vue'
import { pdfjsLib } from '@/utils/pdf'
import { request } from '@/shared/api/request'

interface Props {
  documentUrl?: string
  fileType?: string
  autoLoad?: boolean
  enableSyncScroll?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  autoLoad: true,
  enableSyncScroll: false
})

const emit = defineEmits<{
  loaded: []
  error: [error: string]
  scroll: [scrollTop: number]
  pageChange: [pageNumber: number]
  syncScroll: [scrollTop: number]
}>()

const loading = ref(false)
const error = ref('')
const isMounted = ref(false)
const isReady = ref(false)
const contentContainer = ref<HTMLElement>()
const pdfPagesContainer = ref<HTMLElement>()

const isScrolling = ref(false)
const scrollTimeout = ref<ReturnType<typeof setTimeout>>()
const lastScrollRatio = ref(0)
const isReadyForSync = ref(false)
const lastScrollHeight = ref(0)
const scrollHeightStableCount = ref(0)

const detectedFileType = computed(() => {
  if (!props.documentUrl) return ''
  const url = props.documentUrl.toLowerCase()
  if (url.includes('.pdf')) return 'pdf'
  const match = url.match(/\.([a-z0-9]+)(?:\?|$)/i)
  return match ? match[1].toLowerCase() : ''
})

const fileType = computed(() => {
  return props.fileType || detectedFileType.value || 'pdf'
})

async function loadDocument() {
  if (!props.documentUrl) {
    error.value = '文档 URL 未提供'
    return
  }

  loading.value = true
  error.value = ''
  isReady.value = false
  if (import.meta.env.DEV) {
    console.log('[DocumentViewer] 开始加载 PDF:', props.documentUrl)
  }

  try {
    if (fileType.value !== 'pdf') {
      error.value = `当前仅支持 PDF 预览，格式 .${fileType.value} 请下载后查看`
      loading.value = false
      return
    }

    if (!isMounted.value) {
      await new Promise(resolve => setTimeout(resolve, 100))
    }
    await nextTick()

    const container = pdfPagesContainer.value
    if (!container) {
      loading.value = false
      return
    }
    container.innerHTML = ''

    // 使用项目 Axios 拉取 PDF（与 /users/me、/documents/projects 等走同一 baseURL 与鉴权）
    // 若传入的是完整 URL，则提取相对 baseURL 的 path，保证请求与其它 API 完全同路
    const apiV1 = '/api/v1/'
    const pdfPath = props.documentUrl.includes(apiV1)
      ? props.documentUrl.split(apiV1)[1]
      : props.documentUrl.replace(/^https?:\/\/[^/]+/, '').replace(/^\//, '')
    if (import.meta.env.DEV) {
      console.log('[DocumentViewer] Axios 请求 path:', pdfPath)
    }
    // 注意：request 拦截器在非 blob 时返回 response.data，故此处得到的是 ArrayBuffer 本身
    const pdfData = await request.get(pdfPath, {
      responseType: 'arraybuffer',
      withCredentials: true
    })
    const arrayBuffer = pdfData instanceof ArrayBuffer ? pdfData : (pdfData as { data?: ArrayBuffer })?.data
    if (!arrayBuffer) {
      throw new Error('PDF 响应数据为空')
    }
    if (import.meta.env.DEV) {
      console.log('[DocumentViewer] PDF 已通过 Axios 拉取成功, 大小:', arrayBuffer.byteLength)
    }
    const loadingTask = (pdfjsLib as any).getDocument({ data: arrayBuffer })
    const pdfDoc = await loadingTask.promise
    const numPages = pdfDoc.numPages

    const scale = 1.5
    for (let i = 1; i <= numPages; i++) {
      const page = await pdfDoc.getPage(i)
      const viewport = page.getViewport({ scale })
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (!context) continue
      canvas.height = viewport.height
      canvas.width = viewport.width
      canvas.className = 'pdf-page-canvas'
      await page.render({
        canvasContext: context,
        viewport
      }).promise
      container.appendChild(canvas)
    }

    isReady.value = true
    loading.value = false
    if (props.enableSyncScroll) {
      nextTick(() => {
        setTimeout(() => {
          const scrollContainer = getScrollContainer()
          if (scrollContainer) {
            lastScrollHeight.value = scrollContainer.scrollHeight
            isReadyForSync.value = true
          }
        }, 300)
      })
    }
    emit('loaded')
  } catch (err: any) {
    const msg = err?.message || String(err)
    error.value = msg.includes('fetch') || msg.includes('401') || msg.includes('403')
      ? '文档加载失败：请确认已登录且有权限'
      : msg || '文档加载失败'
    loading.value = false
    isReady.value = false
    emit('error', error.value)
    console.error('[DocumentViewer] 加载失败:', props.documentUrl, err)
  }
}

function getScrollContainer(): HTMLElement | null {
  return contentContainer.value || null
}

function getScrollPosition(): number {
  const el = getScrollContainer()
  return el ? el.scrollTop : 0
}

function getScrollRatio(): number {
  const el = getScrollContainer()
  if (!el) return 0
  const ch = el.clientHeight
  const sh = el.scrollHeight
  if (sh <= ch) return 0
  const max = sh - ch
  return max > 0 ? el.scrollTop / max : 0
}

function handleScroll() {
  if (!props.enableSyncScroll || isScrolling.value || !isReadyForSync.value) return
  const el = getScrollContainer()
  if (!el) return
  isScrolling.value = true
  if (scrollTimeout.value) clearTimeout(scrollTimeout.value)
  scrollTimeout.value = setTimeout(() => {
    const scrollTop = getScrollPosition()
    const ratio = getScrollRatio()
    if (Math.abs(ratio - (lastScrollRatio.value || 0)) > 0.01) {
      lastScrollRatio.value = ratio
    }
    emit('syncScroll', scrollTop)
    isScrolling.value = false
  }, 150)
}

function syncScrollPosition(scrollTop: number) {
  if (!isReadyForSync.value || isScrolling.value) return
  const el = getScrollContainer()
  if (!el) return
  const cur = el.scrollHeight
  if (lastScrollHeight.value > 0 && Math.abs(cur - lastScrollHeight.value) > 50) {
    scrollHeightStableCount.value = 0
    lastScrollHeight.value = cur
    return
  }
  lastScrollHeight.value = cur
  scrollHeightStableCount.value++
  if (scrollHeightStableCount.value < 2) return
  if (Math.abs(scrollTop - el.scrollTop) < 1) return
  isScrolling.value = true
  requestAnimationFrame(() => {
    const e = getScrollContainer()
    if (e) e.scrollTop = scrollTop
    setTimeout(() => { isScrolling.value = false }, 30)
  })
}

function syncToScrollRatio(ratio: number) {
  if (!contentContainer.value || isScrolling.value || !isReadyForSync.value) return
  const el = contentContainer.value
  const ch = el.clientHeight
  const sh = el.scrollHeight
  if (sh <= ch) return
  const max = sh - ch
  const target = Math.max(0, Math.min(1, ratio)) * max
  if (Math.abs(target - el.scrollTop) < 2) return
  isScrolling.value = true
  requestAnimationFrame(() => {
    if (contentContainer.value) {
      contentContainer.value.scrollTop = target
    }
    setTimeout(() => { isScrolling.value = false }, 30)
  })
}

function removeScrollListener() {
  if (scrollTimeout.value) clearTimeout(scrollTimeout.value)
}

function handleDownload() {
  if (props.documentUrl) window.open(props.documentUrl, '_blank')
}

watch(() => props.enableSyncScroll, (val) => {
  if (val && contentContainer.value) {
    lastScrollHeight.value = contentContainer.value.scrollHeight
    isReadyForSync.value = true
  }
})

watch(() => props.documentUrl, async (newUrl, oldUrl) => {
  if (newUrl && props.autoLoad && newUrl !== oldUrl) {
    isReadyForSync.value = false
    scrollHeightStableCount.value = 0
    lastScrollHeight.value = 0
    error.value = ''
    loading.value = true
    isReady.value = false
    await nextTick()
    if (!isMounted.value) await new Promise(r => setTimeout(r, 100))
    await loadDocument()
  } else if (!newUrl) {
    isReady.value = false
    loading.value = false
  }
}, { immediate: false })

onMounted(async () => {
  isMounted.value = true
  await nextTick()
  await new Promise(r => setTimeout(r, 100))
  if (props.autoLoad && props.documentUrl) {
    await loadDocument()
  }
})

onUnmounted(() => {
  isMounted.value = false
  isReady.value = false
  removeScrollListener()
})

defineExpose({
  syncScrollPosition,
  syncToScrollRatio
})
</script>

<style scoped lang="less">
.document-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  .loading-container,
  .error-container,
  .unsupported-type {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: 400px;
    background-color: #f0f2f5;
  }

  .document-content {
    width: 100%;
    height: 100%;
    min-width: 0;
    overflow: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    position: relative;
    background-color: #fff;
  }

  .pdf-pages {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    gap: 16px;

    :deep(.pdf-page-canvas) {
      max-width: 100%;
      height: auto;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
